import { ISmplPose } from '@seddi/avatar';
import { createSceneFromFit, fitroomViewportModuleContext } from '@seddi/ui-babylon-fitroom';
import log from 'loglevel';
import { fetchFit, fetchPutFit } from '../../closets';
import {
  Garment,
  GarmentExt,
  Style,
  fetchGarment,
  fetchStyle,
  fetchStylePoses,
} from '../../garments';
import { MessageType, sendMessage } from '../../iframe';
import { homeActions } from '../slices/homeSlice';
import { homeStateExt } from '../store/homeStateExt';
import { HomeDispatch, HomeGetState } from '../store/homeStore';
import { beginFitPoll } from './homeFitPoll';
import { recommendVariantSize } from './homeSizeRecommendation';
import { setPose } from './homePose';

/**
 * Update style chunk.
 * @param dispatch Redux dispatch function.
 * @param getState Redux state accessor.
 * @param style Style.
 * @param stylePoses Style poses.
 * @param variant Variant.
 */
const updateStyle = async (
  dispatch: HomeDispatch,
  getState: HomeGetState,
  style: Style,
  stylePoses: ISmplPose[],
  variant: string,
) => {
  const { poseId, style: currStyle } = getState();

  // No update.
  if (currStyle?.id === style.id && currStyle?.variant === variant) {
    return;
  }

  // Update state.
  homeStateExt.style = style;
  homeStateExt.stylePoses = stylePoses;
  dispatch(homeActions.setStyleVariant({ id: style.id, variant }));

  // Update poses selection from style.
  if (currStyle?.id === style.id || stylePoses.length === 0) {
    return;
  }

  const needsUpdate = stylePoses.every(p => p.id !== poseId);
  if (needsUpdate) {
    log.trace('[Fitroom] Set pose from style');
    await setPose(dispatch, getState, stylePoses[0].id);
  }
};

/**
 * Update garment chunk.
 * @param dispatch Redux dispatch function.
 * @param getState Redux state accessor.
 * @param garment Garment.
 */
export const updateGarment = async (
  dispatch: HomeDispatch,
  getState: HomeGetState,
  garment: GarmentExt,
) => {
  const { avatarId, poseId } = getState();

  // Update state.
  homeStateExt.garment = garment;
  dispatch(homeActions.setGarment(garment.id));

  // Send garment updated event.
  sendMessage({ type: MessageType.GARMENT_UPDATED_EVENT, payload: garment.id });

  if (!avatarId || !poseId) {
    return;
  }

  // 0. try get garment fit
  // 1. scene avatar coverage update
  let [fit] = await Promise.all([
    fetchFit(avatarId, garment.id, poseId),
    // updateSceneAvatarTextures(dispatch, {
    //   isBottomDisabled: false,
    //   isTopDisabled: false,
    // }),
  ]);

  // Create garment fit
  if (!fit) {
    fit = await fetchPutFit(avatarId, garment.id, poseId);
  }

  if (fit.geometrySurfaceStatus !== 'COMPLETED') {
    // garment fit polling
    beginFitPoll(dispatch, avatarId, garment.id, poseId);
    return;
  }

  // Show garment on viewport
  const scene = createSceneFromFit(garment, fit)!;
  const context = fitroomViewportModuleContext();
  context.updateGarment(scene);
};

/**
 * Update garment recommendation chunk.
 * @param dispatch Redux dispatch function.
 * @param getState Redux state accessor.
 * @param id Garment ID.
 */
const updateGarmentRecommendation = async (
  dispatch: HomeDispatch,
  getState: HomeGetState,
  style: Style,
  variant: string,
) => {
  // Style variant size recommendation.
  const { avatarId, poseId } = getState();
  if (!avatarId || !poseId) {
    return;
  }

  const avatar = homeStateExt.avatar!;
  let garmentSummary!: Garment;

  try {
    garmentSummary = await recommendVariantSize(dispatch, avatar, style, variant);
  } catch (error) {
    log.error(`Error fetching style recommendation. ${error}`);
    return;
  }

  // Update garment.
  const garment = await fetchGarment(garmentSummary.id);
  await updateGarment(dispatch, getState, garment);
};

/**
 * Set garment flow.
 * @param dispatch Redux dispatch function.
 * @param getState Redux state accessor.
 * @param id Garment ID.
 */
export const setGarment = async (dispatch: HomeDispatch, getState: HomeGetState, id: string) => {
  // Extract needed style and variant information.
  let garment!: GarmentExt;
  let style!: Style;
  let stylePoses!: ISmplPose[];

  try {
    // Find garment and style.
    garment = await fetchGarment(id);
    const styleId = garment.containerId;
    const [style_, poses_] = await Promise.all([fetchStyle(styleId), fetchStylePoses(styleId)]);
    style = style_;
    stylePoses = poses_;
  } catch (error) {
    log.error(`Error fetching style. ${error}`);
    return;
  }

  // Update style variant.
  await updateStyle(dispatch, getState, style, stylePoses, garment.variant);

  // Update garment.
  await updateGarment(dispatch, getState, garment);
};

/**
 * Set garment recommendation flow.
 * @param dispatch Redux dispatch function.
 * @param getState Redux state accessor.
 * @param id Garment ID.
 */
export const setGarmentRecommendation = async (
  dispatch: HomeDispatch,
  getState: HomeGetState,
  id: string,
) => {
  // Extract needed style and variant information.
  let garment!: GarmentExt;
  let style!: Style;
  let stylePoses!: ISmplPose[];

  try {
    // Find garment and style.
    garment = await fetchGarment(id);
    const styleId = garment.containerId;
    const [style_, poses_] = await Promise.all([fetchStyle(styleId), fetchStylePoses(styleId)]);
    style = style_;
    stylePoses = poses_;
  } catch (error) {
    log.error(`Error fetching style. ${error}`);
    return;
  }

  // Update style variant.
  await updateStyle(dispatch, getState, style, stylePoses, garment.variant);

  // Update garment recommendation.
  await updateGarmentRecommendation(dispatch, getState, style, garment.variant);
};

/**
 * Set garment recommendation from sku flow.
 * @param dispatch Redux dispatch function.
 * @param getState Redux state accessor.
 * @param sku Garment SKU.
 */
export const setGarmentRecommendationFromSku = async (
  dispatch: HomeDispatch,
  getState: HomeGetState,
  sku: string,
) => {
  // TODO: Garment recommendation from SKU.
};

/**
 * Set style variant flow.
 * @param dispatch Redux dispatch function.
 * @param getState Redux state accessor.
 * @param variant Variant.
 */
export const setStyleVariant = async (
  dispatch: HomeDispatch,
  getState: HomeGetState,
  variant: string,
) => {
  const { garmentId, style: styleState } = getState();

  // Ignore when no style.
  if (!styleState) {
    return;
  }

  // Update style variant.
  const style = homeStateExt.style!;
  const stylePoses = homeStateExt.stylePoses!;
  await updateStyle(dispatch, getState, style, stylePoses, variant);

  // Preserve size when posible.
  if (garmentId) {
    const garment = homeStateExt.garment!;
    const size = garment.size;
    const next = style.variants.find(g => g.size === size && g.variant === variant);

    // Update garment.
    if (next) {
      const garment = await fetchGarment(next.id);
      await updateGarment(dispatch, getState, garment);
      return;
    }
  }

  // Update garment recommendation.
  await updateGarmentRecommendation(dispatch, getState, style, variant);
};
